import { NavLink, Outlet, useLocation } from "react-router";
import { Compass, Ellipsis, NotebookPen, NotebookText, Search, Sun, Wallet } from "lucide-react";
import { useRef, useState } from "react";
import { useSession } from "./session.tsx";
import { useTrip } from "./trip.tsx";
import { minutesAgoLabel } from "@shared/time.ts";
import { packAgeLabel, readPackMeta } from "../lib/offline.ts";
import { MenuSheet } from "../components/MenuSheet.tsx";

const links = [
  { to: "/today", label: "Today", icon: Sun },
  { to: "/journey", label: "Journey", icon: Compass },
  { to: "/money", label: "Money", icon: Wallet },
  { to: "/bookings", label: "Bookings", icon: NotebookText },
  { to: "/essentials", label: "Essentials", icon: NotebookPen },
];

export function AppShell() {
  const { role, offlineReader } = useSession();
  const { trip, offline, error, loading, conflict, reloadRemote, reapplyDraft, refresh, lastUpdatedAt, syncState, changeNotice, refreshing } = useTrip();
  const location = useLocation();
  const [menuOpen, setMenuOpen] = useState(false);
  const menuButtonRef = useRef<HTMLButtonElement>(null);
  const pack = readPackMeta();

  const freshness = syncState === "offline" || offlineReader
    ? pack.ready
      ? `Offline copy. ${packAgeLabel(pack)}.`
      : "Offline pack on this phone."
    : syncState === "session_expired"
      ? "Sign in again to edit. The pack can still be read."
      : syncState === "server_unavailable"
        ? "Could not reach the server."
        : lastUpdatedAt
          ? minutesAgoLabel(lastUpdatedAt) ?? "Checked just now"
          : "Shared trip";
  const packStale = pack.ready && trip && pack.revision != null && pack.revision < trip.revision;

  return (
    <div className="app-shell">
      <aside className="desktop-nav">
        <p className="wordmark">Thailand, together</p>
        {links.map((link) => (
          <NavLink key={link.to} to={link.to} className={({ isActive }) => (isActive ? "active" : "")}>
            {link.label}
          </NavLink>
        ))}
        <NavLink to="/search">Search</NavLink>
        <NavLink to="/map">Map</NavLink>
        {role === "owner" ? <NavLink to="/editor">Edit</NavLink> : null}
        <NavLink to="/essentials/phone">This phone</NavLink>
        <NavLink to="/updates">Updates</NavLink>
      </aside>
      <div>
        <div className="util-bar">
          <p className="wordmark">Thailand, together</p>
          <div className="util-actions">
            <NavLink className="btn-icon" to="/search" aria-label="Search">
              <Search size={18} />
            </NavLink>
            <button
              className="btn-icon"
              type="button"
              aria-label="Menu"
              aria-expanded={menuOpen}
              ref={menuButtonRef}
              onClick={() => setMenuOpen((open) => !open)}
            >
              <Ellipsis size={18} />
            </button>
          </div>
        </div>
        {menuOpen ? (
          <MenuSheet
            role={role}
            onClose={() => setMenuOpen(false)}
            onRefresh={() => void refresh()}
            trigger={menuButtonRef}
          />
        ) : null}
        <div inert={menuOpen || undefined}>
        <p className="sync-line">
          {refreshing ? "Checking for updates…" : freshness}
          {packStale ? " Offline copy needs updating." : ""}
        </p>
        {changeNotice ? (
          <div className="banner" style={{ margin: "12px 20px" }}>
            <p>{changeNotice.summary}</p>
            {changeNotice.href ? <NavLink to={changeNotice.href}>Open the change</NavLink> : null}
          </div>
        ) : null}
        {offline || (error && !conflict) ? <div className="banner warn" style={{ margin: "12px 20px" }}>{error ?? "Showing the downloaded pack."}</div> : null}
        {conflict ? (
          <div className="banner warn" style={{ margin: "12px 20px" }}>
            <p>Another save landed first. Your draft is still here.</p>
            <div className="row-actions">
              <button className="btn-secondary" type="button" onClick={reloadRemote}>Reload their version</button>
              <button className="btn" type="button" onClick={() => void reapplyDraft()}>Reapply my draft</button>
            </div>
          </div>
        ) : null}
        {trip?.fixtureKind === "synthetic" ? (
          <div className="banner warn" style={{ margin: "12px 20px" }}>
            Preview fixture only - not the family bookings.
          </div>
        ) : null}
        {loading && !trip ? (
          <main className="page">
            <div className="skeleton" style={{ height: 220, marginBottom: 16 }} />
            <div className="skeleton" style={{ height: 88 }} />
          </main>
        ) : (
          <Outlet />
        )}
        </div>
      </div>
      <nav className="nav-bottom" aria-label="Main" inert={menuOpen || undefined}>
        {links.map((link) => {
          const Icon = link.icon;
          const active = location.pathname.startsWith(link.to);
          return (
            <NavLink key={link.to} to={link.to} className={active ? "active" : ""}>
              <Icon size={20} aria-hidden="true" />
              {link.label}
            </NavLink>
          );
        })}
      </nav>
    </div>
  );
}
